import {
  floorFee,
  BPS_DENOMINATOR as MARKET_MATH_BPS_DENOMINATOR,
  splitFloorFee,
} from '@morfi/market-math';
import BN from 'bn.js';

/**
 * The on-chain fee configuration, mirroring the `fee_bps_*` fields of the
 * markets program `MarketConfig`
 * (`contracts/markets/programs/markets/src/state/market_config.rs`).
 *
 * These are the ONLY fee parameters that exist on-chain. There is no referral
 * fee, no LP fee, no conviction-dynamic fee, and no tiered post-resolution fee.
 *
 * All values are basis points (BPS, 1/10000).
 */
export interface MarketFeeConfig {
  /** Buy fee, charged on top of `quoteIn` (split backers/platform). */
  feeBpsBuy: number;
  /** Base sell fee, deducted from gross sell proceeds (split backers/platform). */
  feeBpsSellNormal: number;
  /**
   * Extra sell fee leg applied only when the seller is on a non-target outcome
   * of a market with an active penalty target. Routed entirely to the prize pool.
   */
  feeBpsSellPenaltyExtra: number;
  /** Backer-fee share rate applied to backing deposits. */
  feeBpsBacking: number;
  /** Fee deducted from quote redeemed by an unback operation. */
  feeBpsUnbacking: number;
  /**
   * Flat prize-pool fee charged on every trade. On buys it is charged on top of
   * `quoteIn`; on sells it is deducted from gross proceeds.
   */
  feeBpsPrizePool: number;
}

/** Backers/platform split of a base fee, mirroring the on-chain `apply_fee_split`. */
export interface FeeSplit {
  /** Portion routed to backers (D6). */
  backerPortion: BN;
  /** Portion routed to the platform treasury (D6). */
  platformPortion: BN;
}

/**
 * Apply a fee in basis points with floor rounding.
 *
 * Mirrors the on-chain integer fee math
 * (`quote * fee_bps / 10000`, truncating division — see `compute_buy_outputs`
 * / `compute_sell_outputs` in `instructions/trading/math.rs`).
 *
 * @param amount - Base amount the fee is computed on (D6)
 * @param feeBps - Fee rate in basis points (e.g. 100 = 1%)
 * @returns Fee amount (D6, floor-rounded)
 */
export function feeForBps(amount: BN, feeBps: number): BN {
  return bnFromBigint(
    floorFee(BigInt(amount.toString(10)), BigInt(feeBps), MARKET_MATH_BPS_DENOMINATOR),
  );
}

/**
 * Split a base fee between backers and the platform, mirroring the on-chain
 * `apply_fee_split` (`contracts/markets/programs/markets/src/math`).
 *
 * Formula: `backerPortion = floor(fee * backersBps / 10000)`,
 * `platformPortion = fee - backerPortion` (remainder, so the split is exact).
 *
 * @param fee - Total base fee to split (D6)
 * @param feeSplitBackersBps - Backers' share of the fee in basis points
 * @returns {@link FeeSplit} with backerPortion and platformPortion (both D6)
 */
export function applyFeeSplit(fee: BN, feeSplitBackersBps: number): FeeSplit {
  const split = splitFloorFee(
    BigInt(fee.toString(10)),
    BigInt(feeSplitBackersBps),
    MARKET_MATH_BPS_DENOMINATOR,
  );
  return {
    backerPortion: bnFromBigint(split.ratedAmount),
    platformPortion: bnFromBigint(split.remainderAmount),
  };
}

function bnFromBigint(value: bigint): BN {
  return new BN(value.toString());
}
